import type { IconType } from "react-icons";
import {
	FiActivity,
	FiBarChart2,
	FiBook,
	FiBox,
	FiCloud,
	FiCornerUpRight,
	FiFileText,
	FiFolder,
	FiGitBranch,
	FiGlobe,
	FiGrid,
	FiLayers,
	FiLayout,
	FiList,
	FiKey,
	FiMail,
	FiMap,
	FiPenTool,
	FiRefreshCw,
	FiRotateCcw,
	FiServer,
	FiSettings,
	FiShare2,
	FiShield,
	FiShoppingBag,
	FiTag,
	FiTrendingUp,
	FiUsers,
	FiWifi
} from "react-icons/fi";

export interface TableColumn<T> {
	key: keyof T | string; // can be a property of T or a virtual key
	label: string;
	render?: (item: T, history?: any, refresh?: () => Promise<void>) => React.ReactNode;
}

// Build-time fallback. The runtime value resolved from `/api/public-config`
// lives on `window.__APA_RUNTIME_CONFIG__.resourcesPublicBaseUrl` (set by
// `index.tsx` before React renders). `Helper/storage.tsx` reads the runtime
// value when present and falls back to this constant when not.
export const STORAGE_PUBLIC_BASE_URL = "https://apa-parts.resources.pulsarinteractive.cloud";
export const STORAGE_INTERNAL_BASE_URL = "https://preview.apa-parts.content.pulsarinteractive.cloud";
export const STORAGE_INTERNAL_HEADER = "INTERNAL-USE-DF45F4G98938922115452*_!:;,h";

/** Base URL of the external public content API (APA.PUBLIC.API), used by the
 * "API publique" page's domain panel + live tester. Overridable at build time
 * via REACT_APP_PUBLIC_API_URL; defaults to the deployed production host. */
export const PUBLIC_API_BASE_URL = (
	process.env.REACT_APP_PUBLIC_API_URL?.trim() ||
	"https://public-api.apa-parts.internal.pulsarinteractive.io"
).replace(/\/+$/, "");

declare global {
	interface Window {
		__APA_RUNTIME_CONFIG__?: {
			resourcesPublicBaseUrl?: string | null;
			publicResourcesBaseUrl?: string | null;
		};
	}
}

/** Returns the runtime-resolved public storage base URL or falls back to the
 * build-time constant. Called by `Helper/storage.tsx` on every URL resolution
 * so the value follows changes (e.g. once the runtime fetch lands). */
export const getStoragePublicBaseUrl = (): string => {
	if (typeof window !== "undefined") {
		const runtime = window.__APA_RUNTIME_CONFIG__?.resourcesPublicBaseUrl;
		if (typeof runtime === "string" && runtime.trim()) return runtime.trim();
	}
	return STORAGE_PUBLIC_BASE_URL;
};


export type NavItem = {
	type: "item";
	to: string;
	label: string;
	icon: IconType;
};

export type NavEntry =
	| {
			type: "section";
			label: string;
	  }
	| NavItem
	| {
			type: "group";
			key: string;
			label: string;
			to: string;
			icon: IconType;
			children: NavItem[];
	  };

export const NAV_ENTRIES: NavEntry[] = [
	{ type: "section", label: "Overview" },
	{ type: "item", to: "/dashboard", label: "Dashboard", icon: FiGrid },

	{ type: "section", label: "Boutique" },
	{ type: "item", to: "/boutique", label: "Tableau de bord", icon: FiBarChart2 },
	{ type: "item", to: "/orders", label: "Commandes", icon: FiShoppingBag },
	{ type: "item", to: "/customers", label: "Clients", icon: FiUsers },
	{ type: "item", to: "/returns", label: "Consignes & retours", icon: FiRotateCcw },
	{ type: "item", to: "/stock", label: "Stock", icon: FiBox },
	{ type: "item", to: "/quotes", label: "Devis (Pro)", icon: FiFileText },

	{ type: "section", label: "Content" },
	{
		type: "group",
		key: "content",
		label: "Content",
		to: "/content",
		icon: FiFileText,
		children: [
			{ type: "item", to: "/pages", label: "Pages", icon: FiFileText }
		]
	},
	{ type: "item", to: "/traduction", label: "Traduction", icon: FiGlobe },
	// The layout / languages / contact-info singletons sit at the bottom of the Content section,
	// below Traduction — they're rarely-touched config, not day-to-day content.
	{ type: "item", to: "/layout", label: "Layout", icon: FiLayout },
	{ type: "item", to: "/languages", label: "Languages", icon: FiBook },
	{ type: "item", to: "/contact-info", label: "Contact Info", icon: FiMail },

	{ type: "section", label: "Engagement" },
	{ type: "item", to: "/newsletters", label: "Newsletters", icon: FiMail },
	{ type: "item", to: "/contact-messages", label: "Contact Messages", icon: FiBook },

	{ type: "section", label: "Monitoring" },
	{ type: "item", to: "/network-status", label: "Network Status", icon: FiWifi },
	{ type: "item", to: "/metrics", label: "Metrics", icon: FiTrendingUp },
	{ type: "item", to: "/analytics", label: "Analytics", icon: FiActivity },
	{ type: "item", to: "/logs", label: "Logs", icon: FiList },
	{ type: "item", to: "/sitemaps", label: "Sitemaps", icon: FiMap },
	{ type: "item", to: "/backup", label: "Backup", icon: FiRefreshCw },

	{ type: "section", label: "Administration" },
	{ type: "item", to: "/external-api", label: "API interne", icon: FiKey },
	{ type: "item", to: "/deployment", label: "Deployment", icon: FiServer },
	{ type: "item", to: "/fragments", label: "Fragments", icon: FiBox },
	{ type: "item", to: "/deleted-pages", label: "Archive content", icon: FiRotateCcw },
	{ type: "item", to: "/template", label: "Template", icon: FiLayers },
	{ type: "item", to: "/users", label: "Users", icon: FiUsers },
	{ type: "item", to: "/permissions", label: "Permissions", icon: FiShield },
	{ type: "item", to: "/storage", label: "Storage", icon: FiCloud },
	{ type: "item", to: "/redirections", label: "Redirections", icon: FiCornerUpRight },
	{ type: "item", to: "/migration", label: "Migration", icon: FiRefreshCw },
	{ type: "item", to: "/settings", label: "Settings", icon: FiSettings },

	// Structure & reference — the data model + taxonomy/tagging + the site map. Not day-to-day content,
	// so it sits below Administration rather than cluttering the Content area.
	{ type: "section", label: "Structure" },
	{ type: "item", to: "/content-types", label: "Content Types", icon: FiLayers },
	{ type: "item", to: "/taxonomie", label: "Taxonomie", icon: FiGitBranch },
	{ type: "item", to: "/categories", label: "Categories", icon: FiFolder },
	{ type: "item", to: "/search-tags", label: "Search Tags", icon: FiTag },
	{ type: "item", to: "/architecture", label: "Architecture", icon: FiShare2 },
	{ type: "item", to: "/design-library", label: "Design Library", icon: FiPenTool }
];
